import React, { useEffect, useReducer } from "react";
import U2000DetailTable from "../../../../../u2000Core/components/tables/detail/U2000DetailTable";
import { useTranslation } from "react-i18next";
import U2000ActionBtn from "../../../../../u2000Core/components/buttons/U2000ActionBtn";
import { FaPlus, FaRegFile, FaTrashAlt } from "react-icons/fa";
import U2000ResponsiveToolbar from "../../../../../u2000Core/components/toolbar/U2000ResponsiveToolbar";

import { useU2000AuthContext } from "../../../../../u2000Core/context/auth/U2000UseAuthContext";
import { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import type { TFunction } from "i18next";
import { U2000IdColumnDef, type U2000TableColumn } from "../../../../../u2000Core/components/tables/basic/U2000BasicTableList";
import u2000FormState, { U2000FormStateEnumState, type U2000FormState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import { U2000StringArrayDisplay } from "../../../../../u2000Core/components/tables/display/U2000StringArrayDisplay";
import useU2000AxiosAuthenticated from "../../../../../u2000Core/axios/U2000UseAxiosAuthenticated";
import { NS, U2103ROLES } from "../../../../U2103";
import U2103UserActionEditDlg from "./U2103UserActionEditDlg";
import U2103UserActionDeleteDlg from "./U2103UserActionDeleteDlg";
import { emptyLogicalUnit, type U2103LogicalUnit, type U2103UserLogicalUnit } from "../../../../U2103Service";
import { u2103UserActionHandler } from "./U2103UserActionHandlers";
import type { U2103UserFormExtendedHandlers } from "../../../U2103UserForm";
import { u2000IntFormState, useU2000FormStateHandlers, type U2000FormSBusinessHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";

// Typage des props du composant
interface U2103RoleActionsProps {
  u2103UserFormHandlers: U2103UserFormExtendedHandlers;
}

// Définition des colonnes pour le tableau des actions d'une unité logique
function getColumnsDef(t: TFunction): U2000TableColumn[] {
  return [
    U2000IdColumnDef,
    {
      name: "logicalUnitCode",
      label: t("lblLogicalUnitCode", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      isDefaultSortedColumn: true,
      widthClass: "w-[15%]",
      type: "string",
      align: "left",
    },
    {
      name: "logicalUnitDescription",
      label: t("lblLogicalUnitDescription", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[27%]",
      type: "string",
      align: "left",
    },
    {
      name: "actions",
      label: t("lblActions", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[60%]",
      type: "string",
      align: "left",
      display: (column, value, row) => {
        return <U2000StringArrayDisplay column={column} value={value} row={row} />;
      },
    },
  ];
}

// Composant principal pour la gestion des actions d'une unité logique
const U2103UserActionsBody: React.FC<U2103RoleActionsProps> = ({ u2103UserFormHandlers }) => {
  // Translation
  const { t } = useTranslation();
  const { hasRole } = useU2000AuthContext();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();

  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);

  const gridStateHandlers = useU2000GridStateHandlers<U2103UserLogicalUnit>();

  const validateUserAction = (logicalUnit: U2103LogicalUnit) => {
    const result: Record<string, string> = {};
    if (logicalUnit.logicalUnitCode.trim() === "") {
      result["logicalUnitCode"] = "Logical Unit Code is required.";
    }
    return result;
  };

  const u2103UserActionBL: U2000FormSBusinessHandlers<U2103LogicalUnit> = {
    newRecord: () => {
      return emptyLogicalUnit;
    },

    // validation du formulaire
    validateForm: validateUserAction,
  };

  const u2103UserActionformStateHandlers = useU2000FormStateHandlers<U2103LogicalUnit>({
    formState,
    formStateDispatch,
    businessHandlers: u2103UserActionBL,
  });

  const u2103UserActionHandlers = u2103UserActionHandler({
    u2000AxiosAuthenticated,
    gridStateHandlers,
    t,
    formStateHandlers: u2103UserActionformStateHandlers,
  });

  // Transposer les données du formStateHandlers au gridStateHandlers pour LogicalUnitActions
  useEffect(() => {
    // Initialiser les colonnes et les données du tableau
    const columns = getColumnsDef(t);
    gridStateHandlers.setColumns(columns);
    gridStateHandlers.setSelectedFields(columns.filter((col) => col.isDefaultColumn).map((col) => col.name));
    gridStateHandlers.setSelectedSortedFields(columns.filter((col) => col.isDefaultSortedColumn).map((col) => ({ name: col.name, sortOrder: "ASC" })));

    // Initialiser les données du tableau
    const logicalUnitActions = u2103UserFormHandlers.getData?.()?.actions as U2103UserLogicalUnit[];
    gridStateHandlers.setData(Array.isArray(logicalUnitActions) ? logicalUnitActions : []);
    gridStateHandlers.setTotalRows(Array.isArray(logicalUnitActions) ? logicalUnitActions.length : 0);

    // Gérer les erreurs spécifiques au champ LogicalUnitActions
    const errorsField = u2103UserFormHandlers.getOtherErrors()?.roleActions;
    if (errorsField !== undefined) {
      gridStateHandlers.setErrors(Array.isArray(errorsField) ? errorsField : errorsField ? [errorsField] : []);
    } else {
      gridStateHandlers.setErrors([]);
    }
  }, [u2103UserFormHandlers, t]);

  // Mettre à jour le formStateHandlers parent lorsque les données du gridStateHandlers changent
  useEffect(() => {
    if (u2103UserActionHandlers.dataChangedEvent === 0) return;

    const logicalUnitActions = gridStateHandlers.getData();
    u2103UserFormHandlers.setData({
      ...u2103UserFormHandlers.getData?.(),
      actions: logicalUnitActions,
    });
  }, [u2103UserActionHandlers.dataChangedEvent]);

  // Désactiver le bouton de modification si aucun ou plus d'une ligne sélectionnée
  function disabledUpdateBtn(): boolean {
    const selectedRows = gridStateHandlers.getSelectedRows();
    return u2103UserFormHandlers.getFormState?.() === U2000FormStateEnumState.READ || selectedRows.length !== 1 ? true : false || "resetPwd" in (u2103UserFormHandlers.getCustomAttributes?.() ?? {});
  }

  // Désactiver le bouton de suppression si aucune ligne sélectionnée
  function disableDeleteBtn(): boolean {
    const selectedRows = gridStateHandlers.getSelectedRows();
    return u2103UserFormHandlers.getFormState?.() === U2000FormStateEnumState.READ || selectedRows.length === 0 ? true : false || "resetPwd" in (u2103UserFormHandlers.getCustomAttributes?.() ?? {});
  }

  // Préparer l'array d'actions pour le toolbar responsive
  const actions = React.useMemo(
    () => [
      {
        key: "add",
        label: t("btnAdd", { ns: NS }),
        handleClick: u2103UserActionHandlers.onAdd,
        hasRole: hasRole(U2103ROLES.ACTIONS),
        disabled: u2103UserFormHandlers.getFormState?.() === U2000FormStateEnumState.READ || "resetPwd" in (u2103UserFormHandlers.getCustomAttributes?.() ?? {}),
        icon: FaPlus,
        Component: U2000ActionBtn,
      },
      {
        key: "modify",
        label: t("btnModify", { ns: NS }),
        handleClick: u2103UserActionHandlers.onModify,
        hasRole: hasRole(U2103ROLES.ACTIONS),
        disabled: disabledUpdateBtn(),
        icon: FaRegFile,
        Component: U2000ActionBtn,
      },
      {
        key: "delete",
        label: t("btnDelete", { ns: NS }),
        handleClick: u2103UserActionHandlers.onDelete,
        hasRole: hasRole(U2103ROLES.ACTIONS),
        disabled: disableDeleteBtn(),
        icon: FaTrashAlt,
        Component: U2000ActionBtn,
      },
    ],
    [t, u2103UserActionHandlers]
  );

  // Render
  return (
    <>
      <U2103UserActionEditDlg u2103UserActionHandler={u2103UserActionHandlers} t={t} NS={NS} />
      <U2103UserActionDeleteDlg u2103UserActionHandler={u2103UserActionHandlers} t={t} NS={NS} />

      <div className="mt-1 mb-1 flex items-center gap-x-2 border-b-2 pb-3">
        <U2000ResponsiveToolbar actions={actions} />
      </div>
      <U2000DetailTable
        gridStateHandlers={gridStateHandlers}
        allowRowSelection={u2103UserFormHandlers.getFormState?.() === U2000FormStateEnumState.EDIT}
        label={t("lblUserAction", {
          count: u2103UserActionHandlers.gridStateHandlers.getTotalRows(),
          ns: NS,
        })}
      />
    </>
  );
};

export default U2103UserActionsBody;
